import React, { FC, useEffect, useState } from "react";
import { View, useWindowDimensions, TouchableOpacity, FlatList, ActivityIndicator, RefreshControl, StyleSheet, Keyboard, DeviceEventEmitter } from "react-native";
import Popover from "react-native-popover-view/dist/Popover";
import { useIsFocused } from "@react-navigation/native";

import { ZenBadge, ZenButton, ZenFilterModal, ZenIcon, ZenToast, ZenSearchBar, ZenEmptyComponent, ZenLoader } from "zen-core-elements";
import { config } from "../../../shared/environment";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { appColors, appFonts, fontSize } from "../../../themes/appTheme";
import CommonHeader from "../../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../../shared/commonComponents/commonDialogBox";
import { checkPosLocationMapping, deleteLocationData, getAllLocationList } from "../../../redux/slices/locationSlice";
import { FilterPayloadProps, LocationProps, PayloadProps } from "./locationModule";
import ZenText from "zen-core-elements/src/elements/zenText/zenText";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";
import { useAppNavigation } from "../../../types/@react-navigation/navigation";
import { LocationStyle } from "../../../themes/components/location/locationStyle";
import { buyXgetYDiscountListStyle } from "../../../themes/components/discounts/buyXgetYDiscountListStyle";
import { useKeyborad } from "../../../shared/hooks/useKeyborad";
import useBackHandler from "../../../shared/hooks/useBackHandler";
import { useTheme } from "@rneui/themed";
import { ListToolbar } from "../../../shared/commonComponents/listToolbar";
import { shallowEqual } from "react-redux";

const LIMIT = 10;

const LocationList: FC = () => {
    const { isWrite: isSettingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
    useBackHandler()
    const { width, height } = useWindowDimensions();
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const isFocused = useIsFocused();
    const keyboardOpen = useKeyborad();

    const [search, setSearch] = useState<string | null>(null);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [filterVisible, setFilterVisible] = useState<boolean>(false);
    const [count, setCount] = useState<number>(0);
    const [deletePopover, setDeletePopover] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<LocationProps | any>();
    const [filterPayLoad, setFilterPayLoad] = useState<FilterPayloadProps>(null);
    const [offset, setOffset] = useState<number>(0);
    const [footerLoader, setFooterLoader] = useState<boolean>(false);

    const locations = useAppSelector(state => state.location.locationData.rows);
    const cachedCount = useAppSelector(state => state.location.locationData.count);
    const isReached = useAppSelector(state => state.location.locationData.isReached);
    const cachedFilter = useAppSelector(state => state.location.locationData.filter);
    const loading = useAppSelector(state => state.location.locationData.loading);

    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: '1',
            filterName: 'Status',
            filterType: "radio",
            filterValue: [{ value: 'Active', label: 'Active' }, { value: 'InActive', label: 'InActive' }],
            initialSelected: ""
        },
        {
            id: '2',
            filterName: 'Medium',
            filterType: "radio",
            filterValue: [{ value: 'POS', label: 'POS' }, { value: 'WEB', label: 'WEB' }],
            initialSelected: ""
        },
    ]);

    const deleteDialogProps = {
        content: `Are you sure you want to remove this location?`,
        button: [{ id: 1, name: "yes" }, { id: 2, name: 'no' }],
        icon: { name: 'delete-outline', color: appColors.red, type: 'material-community' }
    };

    useEffect(() => {
        const locationUpdate = DeviceEventEmitter.addListener("LOCATION_UPDATED", () => {
            setFilterPayLoad(null)
            setSearch(null)
            setOffset(0)
            setSelectedItem(null)
            setDeletePopover(false)
        })
        return () => {
            locationUpdate?.remove()
        }
    }, [])

    const getLocationListAPI = async (offsetVal: number, searchVal: string, payloadVal: FilterPayloadProps, forceRefresh = false) => {
        const payloadData = payloadVal ? JSON.stringify({ status: payloadVal?.status, medium: payloadVal?.medium }) : null;
        const payload: PayloadProps = {
            offset: offsetVal,
            limit: LIMIT,
            searchText: searchVal,
            filterData: payloadData
        };
        const currentFingerprint = JSON.stringify({ searchText: payload.searchText, filterData: payload.filterData });

        if (!forceRefresh && offsetVal === 0 && cachedFilter === currentFingerprint) {
            return;
        }

        await dispatch(getAllLocationList({ storeId: config?.storeId, data: payload })).unwrap()
            .catch((err) => {
                ZenToast({ type: 'error', message: 'Error in get location list' });
                console.log('error in get location logs', err);
            })
            .finally(() => {
                setFooterLoader(false);
                setRefresh(false);
            });
    };

    useEffect(() => {
        if (isFocused && cachedFilter === null) {
            getLocationListAPI(0, search ?? "", filterPayLoad, true);
        }
    }, [isFocused]);

    useEffect(() => {
        const payloadData = filterPayLoad ? JSON.stringify({ status: filterPayLoad?.status, medium: filterPayLoad?.medium }) : null;
        const currentFingerprint = JSON.stringify({ searchText: search ?? "", filterData: payloadData });

        if (cachedFilter !== currentFingerprint && cachedFilter !== null && !refresh) {
            setOffset(0);
            getLocationListAPI(0, search ?? "", filterPayLoad);
        }
    }, [filterPayLoad, cachedFilter]);

    useEffect(() => {
        if (search != null) {
            setOffset(0);
            const debounce = setTimeout(() => {
                getLocationListAPI(0, search, filterPayLoad, true);
            }, 1000);
            return () => clearTimeout(debounce);
        }
    }, [search]);

    useEffect(() => {
        if (selectedItem) {
            onCheckLocationMapping();
        }
    }, [selectedItem]);

    const deleteLocation = () => {
        const props = {
            data: selectedItem?.addressId,
            storeId: config?.storeId,
            id: selectedItem?.id
        };
        dispatch(deleteLocationData(props)).unwrap().then((response) => {
            if (response) {
                ZenToast({ type: 'success', message: 'Location removed successfully' });
                getLocationListAPI(0, search ?? "", filterPayLoad, true);
                setSelectedItem(null);
            }
        }).catch((err) => { console.log('Error in delete location', err) });
    };

    const handleDefaultLocation = (item: LocationProps | undefined) => {
        if (item?.isDefault) {
            ZenToast({ type: "error", message: "You can't remove this location since it is default location." })
        } else {
            setDeletePopover(true);
        }
    };

    const onCheckLocationMapping = () => {
        dispatch(checkPosLocationMapping({ storeId: config?.storeId, id: selectedItem?.id })).unwrap().then((response) => {
            if (response && response?.locationMapping && response?.locationMapping?.locationData) {
                ZenToast({ type: 'error', message: 'You can\'t remove this location since it is associated with some variant stocks.' });
            } else if (response && response?.locationMapping && response?.locationMapping?.userData) {
                ZenToast({ type: 'error', message: 'You can\'t remove this location since it is associated with users.' });
            } else {
                handleDefaultLocation(selectedItem);
            }
        }).catch((err) => { console.log('Error in check location mapping', err) });
    };

    const handleEndReached = () => {
        if (!isReached && !footerLoader && !loading && locations.length < cachedCount) {
            const nextOffset = offset + LIMIT;
            setOffset(nextOffset);
            setFooterLoader(true);
            getLocationListAPI(nextOffset, search ?? "", filterPayLoad);
        }
    };

    const handleRefresh = () => {
        setRefresh(true);
        setOffset(0);
        setSearch(null)
        setFilterPayLoad(null)
        DeviceEventEmitter?.emit("clearFilter")
        getLocationListAPI(0, search ?? "", null, true);
    };

    const confirmation = (value: { name: string }) => {
        if (deletePopover && value) {
            if (value?.name == "yes") {
                setDeletePopover(false);
                deleteLocation();
            } else {
                setDeletePopover(false);
            }
        }
    }
    const { theme } = useTheme()
    const renderItem = ({ item }: { item: LocationProps }) => {
        return (
            <View style={styles.card}>
                <View style={styles.headerArea}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: 10 }}>
                        <View style={styles.locationIconCircle}>
                            <ZenIcon
                                type='material-community'
                                name='map-marker-radius-outline'
                                size={18}
                                color={appColors.primary}
                            />
                        </View>
                        <View style={{ marginLeft: 10, flex: 1 }}>
                            <ZenText content={item.locationName} color={appColors.lightDark} bold={true} numberOfLines={1} ellipsizeMode="tail" size={theme?.mediumFontSize} />
                            {item.isDefault && (
                                <View style={styles.defaultBadge}>
                                    <ZenText content="DEFAULT" size={theme?.badgeFontSize} bold color={appColors.primary} />
                                </View>
                            )}
                        </View>
                    </View>
                    <ZenBadge
                        badgeColor={item.isActive ? appColors.wildGreen : appColors?.wildRed}
                        badgeName={item.isActive ? 'Active' : 'Inactive'}
                        badgeTextColor={appColors?.light}
                        badgeTextSize={"small"}
                        badgeTextStyle={{ fontFamily: appFonts?.bold, paddingHorizontal: 6, paddingVertical: 2 }}
                    />
                </View>

                <View style={styles.divider} />

                <View style={styles.detailsRow}>
                    <View style={[styles.detailCol, { flex: 1 }]}>
                        <ZenText content="Medium" color={appColors.labelColor} size={11} style={{ textAlign: "left" }} />
                        <ZenText content={item.medium ?? '-'} color={appColors.lightDark} bold size={13} style={{ textAlign: "left" }} />
                    </View>

                    <View style={[styles.detailCol, { flex: 1.2 }]}>
                        <ZenText content="Time Zone" color={appColors.labelColor} size={11} style={{ textAlign: "left" }} />
                        <ZenText content={item.timeZone ?? '-'} color={appColors.lightDark} bold size={13} numberOfLines={1} ellipsizeMode="tail" style={{ textAlign: "left" }} />
                    </View>

                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, justifyContent: "flex-end", gap: 8 }}>

                        <TouchableOpacity style={theme.productListStyle.actionButtonEdit} onPress={() => { navigation.navigate('AddEditLocation', { editId: item?.id, locationArrLength: locations?.length, defaultLen: locations.filter((loc) => loc?.isDefault).length }); }} >
                            <ZenIcon name={isSettingWrite ? "pencil" : "eye"} type="octicon" size={theme?.iconSize} color={theme?.primary} />
                        </TouchableOpacity>

                        {isSettingWrite && <TouchableOpacity onPress={() => { setSelectedItem(item); }} style={theme.productListStyle.actionButtonDelete} >
                            <ZenIcon name="delete-outline" type="material-community" size={theme?.iconSize} color={theme?.red} />
                        </TouchableOpacity>}
                    </View>
                </View>
            </View>
        );
    };

    return (
        <View style={{ flex: 1, backgroundColor: "whitesmoke" }}>
            <CommonHeader headerName='Location' backIcon backIconOnpress={() => { navigation?.goBack() }} />

            <ZenFilterModal
                visible={filterVisible}
                setVisible={setFilterVisible}
                filterItems={filterData}
                setFilterItems={setFilterData}
                filterCount={count}
                setFilterCount={setCount}
                onConfirm={(val) => {
                    let data: FilterPayloadProps = {};
                    if (val?.values?.Status) {
                        data.status = val?.values?.Status === 'Active' ? 'true' : val?.values?.Status === 'InActive' ? 'false' : undefined;
                        if (val?.values?.Status === 'Active' || val?.values?.Status === 'InActive') {
                            data.status = val?.values?.Status === 'Active' ? 'true' : 'false';
                        } else {
                            data.status = val?.values?.Status;
                        }
                    }
                    if (val?.values?.Medium) {
                        data.medium = val?.values?.Medium;
                    }
                    setFilterPayLoad(data);
                }}
                title="Location List Filter"
            />



            <ListToolbar
                search={{
                    value: search ?? "",
                    onChange: (val) => setSearch(val as string),
                    onCancel: () => setSearch(null),
                    placeholder: 'Search Location...',
                }}
                filter={{
                    count: count ?? 0,
                    onPress: () => setFilterVisible(true),
                }}
                buttons={isSettingWrite ? [{
                    onPress: () => navigation.navigate('AddEditLocation', { locationArrLength: locations?.length, defaultLen: locations.filter((item) => item?.isDefault).length }),
                    title: "Add Location",
                    variant: "primary"
                }] : []}
            />

            {(loading && !offset && (!refresh || !locations)) ? <ZenLoader lottieType="Local" lottie={require("../../../assets/infinityLoader.json")} /> :
                <View key={width > height ? "grid-view" : "list-view"} style={{ flex: 1 }}>
                    <FlatList
                        data={locations}
                        numColumns={width > height ? 2 : 1}
                        keyExtractor={item => item.id.toString()}
                        renderItem={renderItem}
                        contentContainerStyle={{
                            paddingHorizontal: 6,
                            paddingBottom: locations?.length ? 80 : 0,
                            flexGrow: 1,
                        }}
                        onEndReached={handleEndReached}
                        onEndReachedThreshold={0.5}
                        refreshControl={
                            <RefreshControl refreshing={refresh} onRefresh={handleRefresh} colors={[appColors.primary]} />
                        }
                        ListFooterComponent={
                            footerLoader ? (
                                <ActivityIndicator size="small" color={appColors.primary} style={{ paddingVertical: 12 }} />
                            ) : null
                        }
                        ListEmptyComponent={ZenEmptyComponent}
                    />
                </View>
            }

            {!loading && (width <= height) && isSettingWrite && (
                <View>
                    <View style={buyXgetYDiscountListStyle.plusIconView}>
                        <ZenIcon
                            name="plus"
                            type={"material-community"}
                            size={22}
                            color={appColors.light}
                            onPress={() => { navigation.navigate("AddEditLocation", { locationArrLength: locations?.length, defaultLen: locations.filter((item) => item?.isDefault).length }) }}
                        />
                    </View>
                </View>
            )}

            <Popover isVisible={deletePopover} popoverStyle={[buyXgetYDiscountListStyle.countBadgeContainer, { width: width > 900 ? width / 2 : width > 600 ? 450 : 350 }]}>
                <CommonDialogBox
                    dialog={deleteDialogProps}
                    type="OTHER"
                    confirmation={confirmation}
                />
            </Popover>
        </View>
    );
}

const styles = StyleSheet.create({
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
    },
    card: {
        flex: 1,
        padding: 15,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: appColors.borderColor,
        backgroundColor: appColors.light,
        marginVertical: 6,
        marginHorizontal: 6,
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
    },
    headerArea: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    locationIconCircle: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: '#EEF2FF',
        justifyContent: 'center',
        alignItems: 'center',
    },
    defaultBadge: {
        backgroundColor: '#E0E7FF',
        alignSelf: 'flex-start',
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 4,
        marginTop: 2,
    },
    divider: {
        height: 1,
        backgroundColor: '#F3F4F6',
        marginVertical: 12,
    },
    detailsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
    },
    detailCol: {
        rowGap: 2,
    },
    iconBtn: {
        padding: 8,
        backgroundColor: '#F3F4F6',
        borderRadius: 8
    }
});

export default LocationList;